import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, IconButton, ListItemButton, ListItemIcon, Tooltip, Typography, useTheme } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import { styled, CSSObject, Theme } from '@mui/material/styles';
import React, { useState, useCallback, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useSidebarV2 } from './SidebarV2Context';
import { SIDEBAR_FULL_WIDTH, SIDEBAR_ICON_WIDTH } from './constants';

export type NestedSidebarMode = 'full' | 'icon-only';

export interface NestedSidebarItem {
    id: string;
    label: string;
    icon?: string | React.ReactNode;
    path?: string;
    onClick?: () => void;
}

interface CreateBackNestedSidebarItemOptions {
    onClick: () => void;
    icon?: string | React.ReactNode;
    id?: string;
    label?: string;
}

export const createBackNestedSidebarItem = ({
    onClick,
    icon = <i className="fi fi-br-arrow-left-from-line"></i>,
    id = 'back',
    label = 'Retour',
}: CreateBackNestedSidebarItemOptions): NestedSidebarItem => ({
    id,
    label,
    icon,
    onClick,
});

interface NestedSidebarProps {
    items: NestedSidebarItem[];
    title?: string;
}

const NESTED_FULL_WIDTH = 220;
const NESTED_ICON_WIDTH = 56;

const nestedOpenedMixin = (theme: Theme, offset: number): CSSObject => ({
    width: NESTED_FULL_WIDTH,
    marginLeft: offset,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const nestedClosedMixin = (theme: Theme, offset: number): CSSObject => ({
    width: NESTED_ICON_WIDTH,
    marginLeft: offset,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
});

const NestedDrawer = styled(MuiDrawer, {
    shouldForwardProp: prop => prop !== 'open' && prop !== 'offset',
})<{ offset: number }>(({ theme, open, offset }) => ({
    width: open ? NESTED_FULL_WIDTH : NESTED_ICON_WIDTH,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: open
            ? theme.transitions.duration.enteringScreen
            : theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    ...(open && {
        '& .MuiDrawer-paper': nestedOpenedMixin(theme, offset),
    }),
    ...(!open && {
        '& .MuiDrawer-paper': nestedClosedMixin(theme, offset),
    }),
}));

export const NestedSidebar: React.FC<NestedSidebarProps> = ({ items, title }) => {
    const { mode: parentMode, setNestedSidebarWidth } = useSidebarV2();
    const [nestedMode, setNestedMode] = useState<NestedSidebarMode>('icon-only');
    const location = useLocation();
    const currentPath = location.pathname;
    const theme = useTheme();

    const parentWidth = parentMode === 'full' ? SIDEBAR_FULL_WIDTH : SIDEBAR_ICON_WIDTH;
    const isOpen = nestedMode === 'full';
    const nestedWidth = isOpen ? NESTED_FULL_WIDTH : NESTED_ICON_WIDTH;

    // Register nested sidebar width in context, clear on unmount
    useEffect(() => {
        setNestedSidebarWidth(nestedWidth);
        return () => setNestedSidebarWidth(0);
    }, [nestedWidth, setNestedSidebarWidth]);

    const toggleNestedMode = useCallback(() => {
        setNestedMode(prev => (prev === 'full' ? 'icon-only' : 'full'));
    }, []);

    return (
        <NestedDrawer
            className='nested-drawer'
            variant="permanent"
            open={isOpen}
            offset={parentWidth}
            sx={{
                '& .MuiDrawer-paper': {
                    zIndex: 0,
                    backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(30, 32, 35, 0.95)'
                        : theme.palette.background.paper,
                    borderRight: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : theme.palette.border.main}`,
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            {/* Nested header */}
            <Box sx={{ display: 'flex', justifyContent: isOpen ? 'space-between' : 'center', alignItems: 'center', padding: '16px 12px 8px', minHeight: '48px' }}>
                {title && isOpen && (
                    <Typography
                        variant="labelMediumBold"
                        sx={{ color: theme.palette.lightGrey.main, textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}
                    >
                        {title}
                    </Typography>
                )}
                <IconButton
                    onClick={toggleNestedMode}
                    size="small"
                    sx={{
                        color: theme.palette.lightGrey.main,
                        '&:hover': {
                            background: theme.palette.backgroundPilsurface.main,
                        },
                        ml: isOpen ? 'auto' : 0,
                    }}
                >
                    {isOpen ? (
                        <ChevronLeftIcon sx={{ fontSize: '18px' }} />
                    ) : (
                        <ChevronRightIcon sx={{ fontSize: '18px' }} />
                    )}
                </IconButton>
            </Box>

            {/* Menu items */}
            <Box sx={{ flex: 1, mt: title ? 0 : 2 }}>
                <List sx={{ px: '6px', py: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    {items.map(item => {
                        const isActive = item.path
                            ? currentPath === item.path || currentPath.startsWith(item.path + '/')
                            : false;

                        const button = (
                            <ListItemButton
                                key={item.id}
                                component={item.path ? Link : 'button'}
                                {...(item.path ? { to: item.path } : {})}
                                onClick={item.onClick}
                                selected={isActive}
                                sx={{
                                    '&.Mui-selected': {
                                        background: theme.palette.primary.main,
                                        color: theme.palette.secondary.main,
                                        '&:hover': {
                                            background: theme.palette.primary.main,
                                        },
                                    },
                                    color: theme.palette.lightGrey.main,
                                    borderRadius: '10px',
                                    display: 'flex',
                                    justifyContent: isOpen ? 'flex-start' : 'center',
                                    alignItems: 'center',
                                    gap: '10px',
                                    padding: isOpen ? '10px' : '10px 0',
                                    minHeight: '38px',
                                    '&:hover': {
                                        background: theme.palette.backgroundPilsurface.main,
                                    },
                                }}
                            >
                                {item.icon && (
                                    <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center',  color: isActive ? theme.palette.secondary.main : theme.palette.lightGrey.main, }}>
                                        {typeof item.icon === 'string' ? (
                                            <i
                                                className={item.icon}
                                                style={{
                                                    height: '18px',
                                                    width: '18px',
                                                    color: isActive ? theme.palette.secondary.main : theme.palette.lightGrey.main,
                                                }}
                                            />
                                        ) : (
                                            item.icon
                                        )}
                                    </ListItemIcon>
                                )}
                                {!item.icon && (
                                    <Box
                                        sx={{
                                            width: '6px',
                                            height: '6px',
                                            borderRadius: '50%',
                                            backgroundColor: isActive ? theme.palette.secondary.main : theme.palette.lightGrey.main,
                                            flexShrink: 0,
                                        }}
                                    />
                                )}
                                {isOpen && (
                                    <Typography variant="labelMediumBold" noWrap sx={{ fontSize: '13px' }}>
                                        {item.label}
                                    </Typography>
                                )}
                            </ListItemButton>
                        );

                        if (!isOpen) {
                            return (
                                <Tooltip key={item.id} title={item.label} placement="right" arrow>
                                    {button}
                                </Tooltip>
                            );
                        }

                        return <React.Fragment key={item.id}>{button}</React.Fragment>;
                    })}
                </List>
            </Box>

        </NestedDrawer>
    );
};

/**
 * Hook to calculate the total sidebar offset for main content,
 * accounting for the primary SidebarV2 + optional nested sidebar width.
 */
export const useNestedSidebarOffset = (nestedWidth?: number) => {
    const { sidebarWidth } = useSidebarV2();
    return sidebarWidth + (nestedWidth ?? 0);
};
